import { createAction } from "redux-actions";
import { toast } from "react-toastify";
import { getNewSyncInventoryDownloads } from "src/store/uploads/selector";
import { getJSONFileApi } from "src/apiService/modules/uploads";
import {
  getVintedCatalogIndex,
  type VintedCatalogIndex,
} from "src/apiService/modules/vinted";
import {
  getDepopAttributesIndex,
  type DepopAttributesIndex,
} from "src/apiService/modules/depop";
import { getSyncInventoryDownloadTransform } from "src/utils/uploadTransform";
import type { Download } from "src/interfaces/upload.interface";

import {
  DOWNLOAD,
  UPLOAD,
  RESET,
  FS_CREATE_ITEMS,
  FS_DELETE_ITEMS,
  FS_UPDATE_ITEMS,
  SET_PENDING_INVENTORY_EXISTS,
} from "../common";

export const reset = createAction(UPLOAD + RESET);

export const addUploadRecords = createAction(UPLOAD + FS_CREATE_ITEMS);
export const updateUploadRecords = createAction(UPLOAD + FS_UPDATE_ITEMS);
export const deleteUploadRecords = createAction(UPLOAD + FS_DELETE_ITEMS);

export const addDownloadRecords = createAction(DOWNLOAD + FS_CREATE_ITEMS);
export const updateDownloadRecords = createAction(DOWNLOAD + FS_UPDATE_ITEMS);
export const deleteDownloadRecords = createAction(DOWNLOAD + FS_DELETE_ITEMS);

export const setPendingInventoryExists = createAction(
  SET_PENDING_INVENTORY_EXISTS,
  (exists: boolean) => exists,
);

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message || String(error);
  return String(error);
}

function transformDownloadItems(
  data: unknown,
  type: Download["type"],
  options: {
    vintedCatalogIndex?: VintedCatalogIndex;
    depopAttributesIndex?: DepopAttributesIndex;
    downloadTimestamp?: number;
  },
  filename: string,
) {
  if (!Array.isArray(data)) return [];

  const transform = getSyncInventoryDownloadTransform(type, options);
  const mapped: unknown[] = [];
  for (let i = 0; i < data.length; i++) {
    try {
      mapped.push(transform(data[i]));
    } catch (error) {
      // Skip bad rows; do not fail the whole file / leave the dialog loading.
      console.error("Failed to transform pending inventory item", {
        filename,
        type,
        index: i,
        error,
      });
    }
  }
  return mapped;
}

export function getNewInventoryDownloads() {
  return async (dispatch, getState) => {
    const downloads = getNewSyncInventoryDownloads(getState());
    // Retry previous failures; leave in-flight / completed alone.
    const pendings = downloads.filter(
      (d) => !d.state || !["loading", "done"].includes(d.state),
    );

    if (!pendings.length) return;

    dispatch(
      updateDownloadRecords(
        pendings.map((pending) => ({
          id: pending.id,
          state: "loading" as const,
          error: undefined,
        })),
      ),
    );

    let vintedCatalogIndex: VintedCatalogIndex | undefined;
    if (pendings.some((d) => d.type === "vintedInventory")) {
      try {
        vintedCatalogIndex = await getVintedCatalogIndex();
      } catch (error) {
        console.error("[Vinted] Failed to preload catalog index", error);
      }
    }

    let depopAttributesIndex: DepopAttributesIndex | undefined;
    if (pendings.some((d) => d.type === "depopInventory")) {
      try {
        depopAttributesIndex = await getDepopAttributesIndex();
      } catch (error) {
        console.error("[Depop] Failed to preload attributes index", error);
      }
    }

    const files = await Promise.all(
      pendings.map(async (d) => {
        try {
          const data = await getJSONFileApi(d.filename);
          return {
            id: d.id,
            data: transformDownloadItems(
              data,
              d.type,
              {
                vintedCatalogIndex,
                depopAttributesIndex,
                // Legacy rows without requestDate: Downloads.timestamp beats
                // review-time Date.now() for relative upload_date strings.
                downloadTimestamp: d.timestamp,
              },
              d.filename,
            ),
            state: "done" as const,
            error: undefined,
          };
        } catch (error) {
          const message = errorMessage(error);
          // console.error is captured by GlobalErrorHandlers → Events (type: "error").
          console.error(
            `Failed to load pending inventory download id=${d.id} type=${d.type} filename=${d.filename}: ${message}`,
            error,
          );
          return {
            id: d.id,
            data: [],
            state: "error" as const,
            error: message,
          };
        }
      }),
    );

    dispatch(updateDownloadRecords(files));

    const failed = files.filter((f) => f.state === "error");
    if (failed.length) {
      toast.error(
        failed.length === 1
          ? "Failed to load pending inventory. Please try again or contact support."
          : `Failed to load ${failed.length} pending inventory files. Please try again or contact support.`,
      );
    }
  };
}
